<template>
  <v-container fluid class="attendance-container pa-0 ma-0">
    <!-- Header Section -->
    <v-row v-if="showHeader" class="pa-0 ma-0 mb-4">
      <v-col cols="12" class="pa-0">
        <div class="header-section">
          <!-- Close Button -->
          <v-btn icon small class="header-close-btn" @click="dismissHeader">
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
          <v-container fluid class="px-3">
            <v-row align="center" class="pa-0 ma-0">
              <v-col cols="12" md="8" class="d-flex align-center pa-0">
                <div class="header-icon-wrapper">
                  <v-icon size="32" color="white">mdi-calendar-check</v-icon>
                </div>
                <div class="ml-4">
                  <h1 class="page-title">Productivity</h1>
                  <p class="page-subtitle">
                    Track team attendance and productivity metrics
                  </p>
                </div>
              </v-col>
              <v-col cols="12" md="4" class="text-right pa-0">
                <v-chip class="ma-1 feature-chip" outlined>
                  <v-icon left small>mdi-account-multiple</v-icon>
                  Team Tracking
                </v-chip>
                <v-chip v-if="!$store.state.user.permissions.disableExportList" class="ma-1 feature-chip" outlined>
                  <v-icon left small>mdi-file-export</v-icon>
                  Export
                </v-chip>
              </v-col>
            </v-row>
            <v-row class="mt-4">
              <v-col cols="12" class="pa-0">
                <div class="feature-pills-container">
                  <div class="feature-pill">
                    <v-icon small class="mr-2" color="white"
                      >mdi-clock-outline</v-icon
                    >
                    Active Time
                  </div>
                  <div class="feature-pill">
                    <v-icon small class="mr-2" color="white"
                      >mdi-calendar-range</v-icon
                    >
                    Date Range
                  </div>
                  <div class="feature-pill">
                    <v-icon small class="mr-2" color="white"
                      >mdi-account-search</v-icon
                    >
                    Member Filter
                  </div>
                  <div class="feature-pill">
                    <v-icon small class="mr-2" color="white"
                      >mdi-phone-log</v-icon
                    >
                    Call Tracking
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </div>
      </v-col>
    </v-row>

    <!-- Filter Section -->
    <v-row class="pa-0 ma-0 px-3 mb-4">
      <v-col cols="12">
        <v-card class="modern-card customBorderRadius elevation-0">
          <div class="card-header-green"></div>
          <v-card-title class="filter-card-title">
            <div class="filter-title-copy">
              <v-icon left color="primary">mdi-filter</v-icon>
              <span>Filters</span>
            </div>
            <button
              type="button"
              class="classic-view-toggle productivity-classic-filter-toggle"
              role="switch"
              aria-checked="false"
              @click="goToProductivityV2"
            >
              <span class="classic-view-toggle-label classic-view-classic-label">Classic View</span>
              <span class="classic-view-switch" aria-hidden="true">
                <span class="classic-view-switch-thumb"></span>
              </span>
              <span class="classic-view-toggle-label classic-view-modern-label">Modern View</span>
            </button>
            <div class="office-timing-filter-action">
              <v-tooltip bottom max-width="320">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    class="office-timing-filter-btn"
                    color="primary"
                    outlined
                    style="border-radius: 8px;"
                    v-bind="attrs"
                    @click="$router.push({ path: '/organization' })"
                    v-on="on"
                  >
                    <v-icon left small>mdi-clock-outline</v-icon>
                    Set Office Timing
                  </v-btn>
                </template>
                <span>Office timings are used to calculate attendance and productivity.</span>
              </v-tooltip>
            </div>
          </v-card-title>
          <v-card-text class="pa-4">
            <v-row>
              <v-col cols="12" md="4">
                <v-autocomplete
                  v-model="selectedUser"
                  :items="allUsers"
                  item-text="name"
                  item-value="id"
                  label="Members"
                  hide-details="true"
                  outlined
                  dense
                  multiple
                >
                  <template v-slot:selection="{ item, index }">
                    <span v-if="index === 0">{{ item.name }}</span>
                    <span v-if="index === 1" class="grey--text caption"
                      >(+{{ selectedUser.length - 1 }} others)</span
                    >
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" md="4">
                <v-dialog
                  ref="dialog"
                  v-model="modal"
                  :return-value.sync="dates"
                  persistent
                  width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="dateRange"
                      label="Date Range"
                      prepend-inner-icon="event"
                      hide-details="false"
                      outlined
                      dense
                      readonly
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="dates"
                    color="primarygrad"
                    scrollable
                    range
                  >
                    <v-spacer></v-spacer>
                    <v-btn text color="primary" @click="modal = false"
                      >Cancel</v-btn
                    >
                    <v-btn
                      text
                      color="primary"
                      @click="$refs.dialog.save(dates)"
                      >OK</v-btn
                    >
                  </v-date-picker>
                </v-dialog>
              </v-col>
              <v-col cols="12" md="4" class="d-flex">
                <v-btn
                  color="primary"
                  depressed
                  large
                  style="border-radius: 8px;"
                  class="mr-3"
                  @click="load"
                >
                  <v-icon left>mdi-magnify</v-icon>
                  Search
                </v-btn>
                <v-btn
                  v-if="!$store.state.user.permissions.disableExportList"
                  color="primary"
                  depressed
                  large
                  style="border-radius: 8px;"
                  @click="exportReport"
                >
                  <v-icon left>mdi-file-export</v-icon>
                  Export
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Table Section -->
    <v-row class="pa-0 ma-0 px-3">
      <v-col cols="12">
        <v-card class="modern-card customBorderRadius elevation-0">
          <div class="card-header-green"></div>
          <v-card-title>
            <v-icon left color="primary">mdi-table</v-icon>
            Productivity Data
          </v-card-title>
          <v-card-text v-if="loading">
            <v-row
              class="fill-height"
              align-content="center"
              justify="center"
              style="margin: 0px"
            >
              <v-col class="subtitle-1 text-center" cols="12">
                {{ loadingText }}
              </v-col>
              <v-col cols="6">
                <v-progress-linear
                  color="primary accent-4"
                  indeterminate
                  rounded
                  height="4"
                ></v-progress-linear>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-text v-else-if="!loading && reports.length > 0" class="pa-0">
            <v-simple-table hide-default-footer class="elevation-0 modern-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-center sticky-col">Members</th>
                    <th
                      v-for="(item, i) in rangeOfDates"
                      :key="i"
                      class="text-center"
                    >
                      {{ formatHeaderDate(item) }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, i) in reports" :key="i">
                    <td class="py-3 text-center sticky-col">
                      <v-avatar color="primarygrad" size="40">
                        <v-img
                          v-if="item.pictureId"
                          :src="getImage(item.picture)"
                          alt=""
                        ></v-img>
                        <span v-else class="white--text" style="font-size:16px;font-weight:600;">
                          {{ item.name.substring(0, 1).toUpperCase() }}
                        </span>
                      </v-avatar>
                      <div class="member-name">{{ item.name }}</div>
                    </td>
                    <td
                      v-for="(cell, index) in item.data"
                      :key="index"
                      class="py-2 px-1"
                    >
                      <v-card class="day-cell rounded" flat>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <div class="day-stat" v-bind="attrs" v-on="on">
                              <v-icon
                                small
                                :color="cell.count > 0 ? 'primary' : 'red'"
                              >
                                {{
                                  cell.count > 0
                                    ? "record_voice_over"
                                    : "voice_over_off"
                                }}
                              </v-icon>
                              <span class="stat-label">{{ cell.count > 0 ? "On Duty" : "Off Duty" }}</span>
                            </div>
                          </template>
                          <span>{{ cell.count > 0 ? "On Duty" : "Off Duty" }}</span>
                        </v-tooltip>
                        <v-divider class="day-divider"></v-divider>

                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <div class="day-stat" v-bind="attrs" v-on="on">
                              <v-icon small color="primary">phone_in_talk</v-icon>
                              <span class="stat-label">{{ cell.count }}</span>
                            </div>
                          </template>
                          <span>Total Calls</span>
                        </v-tooltip>

                        <v-divider class="day-divider"></v-divider>

                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <div class="day-stat" v-bind="attrs" v-on="on">
                              <v-icon small color="primary">access_time</v-icon>
                              <span class="stat-label">{{ timeFormatCustom(cell.timeSpentOnCall) }}</span>
                            </div>
                          </template>
                          <span>Active Time</span>
                        </v-tooltip>

                        <v-divider class="day-divider"></v-divider>

                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <div class="day-stat" v-bind="attrs" v-on="on">
                              <v-icon small color="primary">timer_off</v-icon>
                              <span class="stat-label">{{ timeFormatCustom(officeTime - cell.timeSpentOnCall) }} mins</span>
                            </div>
                          </template>
                          <span>Idle Time</span>
                        </v-tooltip>
                      </v-card>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>

          <v-card-text
            v-else-if="!loading && reports.length === 0"
            style="padding: 0px"
          >
            <v-container
              fill-height
              fill-width
              style="text-align: center; width: 80%; display: inline"
            >
              <div style="text-align: center" align="center">
                <div align="center">
                  <v-img
                    :src="summary"
                    style="width: 220px; justify-content: center; opacity: 0.6"
                  ></v-img>
                </div>
                <h3 class="h3">Nothings in Attendance</h3>
              </div>
            </v-container>
          </v-card-text>

          <v-card-actions
            v-if="!loading && reports.length > 0"
            class="d-flex align-center"
            style="padding: 16px"
          >
            <v-spacer></v-spacer>
            <div class="v-data-footer d-flex align-center">
              <div class="v-data-footer__select d-flex align-center">
                <span style="margin-right: 8px">Rows per page:</span>
                <v-select
                  v-model="records"
                  :items="recordsPerPage"
                  hide-details
                  dense
                  solo
                  flat
                  style="max-width: 80px"
                  @change="load(records, 0)"
                ></v-select>
              </div>
              <div class="v-data-footer__icons-before">
                <v-btn
                  :disabled="page == 1 ? true : false"
                  color="primary"
                  fab
                  small
                  text
                  @click="prevSet"
                >
                  <v-icon :size="24">arrow_left</v-icon>
                </v-btn>
              </div>
              <div class="v-data-footer__pagination">
                {{ `${page}/${pageCount} of ${totalRecords}` }}
              </div>
              <div class="v-data-footer__icons-after">
                <v-btn
                  :disabled="page >= pageCount ? true : false"
                  color="primary"
                  fab
                  small
                  text
                  @click="nextSet"
                >
                  <v-icon :size="24">arrow_right</v-icon>
                </v-btn>
              </div>
            </div>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <v-snackbar v-model="snackbar" multi-line :color="color">
      <h3 style="font-weight: 400; font-size: 13px; text-transform: uppercase">
        {{ snackbarText }}
      </h3>

      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment-timezone";
const _ = require("lodash");
import Papa from "papaparse";
import Blob from "blob";
import fb from "../js/firebase";

export default {
  data() {
    return {
      showHeader: false,
      snackbar: false,
      snackbarText: "",
      color: "primary",
      search: "",
      rangeOfDates: [],
      modal: false,
      dates: [],

      selectedUser: [],
      allUsers: [],
      selectedDispos: [],
      showTable: false,
      contactDetails: [],
      records: 5,
      loading: false,
      loadingText: "",
      reports: [],
      recordsPerPage: [5, 10, 20, 30, 50, 100, 150, 200],
      page: 1,
      pageCount: 0,
      totalRecords: 0,
      officeTime: null,
    };
  },
  computed: {
    ...mapGetters(["ENDPOINT", "user", "fieldList", "teams", "selectedTeam"]),
    summary() {
      return require("../assets/summary.png");
    },
    dateRange() {
      if (!this.dates || this.dates.length === 0) return "";
      const a = moment(this.dates[0]);
      const b = this.dates[1] ? moment(this.dates[1]) : a;
      const lo = a.isBefore(b) ? a : b;
      const hi = a.isBefore(b) ? b : a;
      if (lo.isSame(hi, "day")) return lo.format("DD MMM");
      if (lo.isSame(hi, "year")) {
        if (lo.isSame(hi, "month")) {
          return lo.format("DD") + "–" + hi.format("DD MMM");
        }
        return lo.format("DD MMM") + " – " + hi.format("DD MMM");
      }
      return lo.format("DD MMM YY") + " – " + hi.format("DD MMM YY");
    },
    calcDispo() {
      return _.without(
        _.map(this.dispos, (d) => {
          if (
            d.title == "SMS" ||
            d.title == "LINK" ||
            d.title == "EMAIL" ||
            d.title == "NEW"
          ) {
            //do not pass these values
          } else {
            return d;
          }
        }),
        undefined,
      );
    },
  },
  watch: {
    async selectedTeam() {
      try {
        await this.fetchAccounts(); // get accounts
        this.load(); // get the logs by day wise format
      } catch (e) {
        (this.snackbar = false), (this.snackbarText = "");
        this.color = "#c62828";
        return;
      }
    },
  },
  async created() {
    // Check if header was dismissed
    this.showHeader =
      localStorage.getItem("attendanceHeaderDismissed") !== "true";
    this.dates = [
      moment()
        .subtract(6, "days")
        .format("YYYY-MM-DD"),
      moment().format("YYYY-MM-DD"),
    ];
    await this.fetchAccounts();
    await this.load();
    this.officeTime = this.$moment(
      this.$store.state.companyDetails.officeEndTime,
      "HH:mm",
    ).diff(
      this.$moment(this.$store.state.companyDetails.officeStartTime, "HH:mm"),
    );
    this.officeTime = this.$moment.duration(this.officeTime).asMinutes();
  },

  methods: {
    formatHeaderDate(d) {
      return d ? moment(d).format("ddd, DD MMM") : "";
    },
    dismissHeader() {
      this.showHeader = false;
      localStorage.setItem("attendanceHeaderDismissed", "true");
    },
    goToProductivityV2() {
      this.$router.push({ path: "/productivity-v2" });
    },
    timeFormatCustom(valueInMinutes) {
      if (valueInMinutes === 0) {
        return "0 sec";
      }

      var totalSeconds = valueInMinutes * 60;
      var days = Math.floor(totalSeconds / 86400);
      totalSeconds %= 86400;
      var hours = Math.floor(totalSeconds / 3600);
      totalSeconds %= 3600;
      var minutes = Math.floor(totalSeconds / 60);
      var seconds = Math.floor(totalSeconds % 60);

      var result = "";
      if (days > 0) {
        result += days + (days === 1 ? " day " : " days ");
      }
      if (hours > 0) {
        result += hours + (hours === 1 ? " hr " : " hrs ");
      }
      if (minutes > 0) {
        result += minutes + (minutes === 1 ? " min " : " mins ");
      }
      if (seconds > 0) {
        result += seconds + (seconds === 1 ? " sec" : " secs");
      }

      return result.trim();
    },
    /** PAGINATION */
    nextSet() {
      this.page++;
      this.load(
        this.records * this.page,
        this.records * this.page - this.records,
      );
    },
    prevSet() {
      this.page--;
      this.load(
        this.records * this.page,
        this.records * this.page - this.records,
      );
    },
    // Fetch Accounts
    async fetchAccounts() {
      this.allUsers = [];

      try {
        var teams = [];
        teams = await this.getTeamIds();

        var response = await this.$http.post(
          `${this.$store.state.ENDPOINT}/accounts/fetch-v2`,
          {
            teams: teams,
          },
        );
        this.allUsers = response.body;
        if (this.user.role == "Manager" || this.user.role == "Teammanager") {
          this.allUsers.unshift({
            id: "allMembers",
            name: "All",
          });
        }
        if (this.selectedUser.length < 1) {
          this.selectedUser.push(this.allUsers[0].id);
        }
      } catch (e) {
        if (e.body.error && e.body.error.message)
          return this.$swal({ type: "error", text: e.body.error.message });
        else
          return this.$swal({
            type: "error",
            text: "Error while fetching accounts",
          });
      }
    },

    /** Get array of teams, user ids */
    getTeamIds() {
      var ids = [];

      if (this.selectedTeam == "allTeams") {
        ids = _.map(this.teams, "id");
      } else {
        ids.push(this.selectedTeam);
      }

      ids = _.without(ids, "allTeams");
      return ids;
    },
    getUserIds(limit, skip) {
      var users = [];

      if (_.includes(this.selectedUser, "allMembers")) {
        users = _.map(this.allUsers, "id");
      } else {
        users = this.selectedUser;
      }
      users = _.without(users, "allMembers");

      if (limit == undefined || skip == undefined) {
        (limit = this.records), (skip = 0);
      }

      this.totalRecords = users.length;
      // total pages
      this.pageCount = Math.ceil(this.totalRecords / this.records);
      if (this.page > this.pageCount) {
        this.page = 1; //set default
      }
      // for member wise paginate
      users = users.slice(skip, limit);
      return users;
    },

    // Load dates and reports
    async load(limit, skip) {
      try {
        // Dates Range
        this.loading = true;
        this.loadingText = "Please wait, we are processing your data";
        var dates = [];

        var start = moment(this.dates[0]);
        var end = this.dates[1] == undefined ? start : moment(this.dates[1]);
        if (start > end) {
          var temp = start;
          start = end;
          end = temp;
        }
        var diff = end.diff(start, "days") + 1;

        // if range is greater than 30 days
        if (diff > 31) {
          this.snackbar = true;
          this.snackbarText = "Please select a range of 30 days";
          this.color = "#cc9900";
          return;
        }
        for (let i = 0; i < diff; i++) {
          dates.push(
            moment(start)
              .add(i, "days")
              .format("DD-MM-YYYY"),
          );
        }

        this.reports = [];
        var users;
        var search = "";

        //Get users
        users = await this.getUserIds(limit, skip);

        var response = await this.$http.post(
          `${this.$store.state.ENDPOINT}/logs/v2/filterByDays`,
          {
            users: users,
            dates: this.dates,
          },
        );

        response = response.body;

        console.log("RESSSSS", JSON.stringify(response));

        var members = _.uniq(
          _.map(response, (r) => {
            return r._id.accountsId;
          }),
        );

        console.log("members 1", JSON.stringify(members));

        members = _.map(users, (u) => {
          var userExists = _.find(members, u);
          if (userExists) {
            return userExists;
          } else {
            return u;
          }
        });

        console.log("members 2", JSON.stringify(members));

        console.log("dates", dates);

        var eachMembersData = _.map(members, (m) => {
          var user = this.getUserById(m);
          return {
            name: user.name,
            email: user.email,
            picture: user.picture,
            pictureId: user.pictureId,
            data: _.map(dates, (d) => {
              var dx = _.find(response, (r) => {
                return (
                  r._id.accountsId == m &&
                  moment(r._id.day).format("DD-MM-YYYY") == d
                );
              });
              if (dx) {
                return {
                  count: dx.count,
                  duration: (Number(dx.duration) / 1000 / 60).toFixed(1),
                  timeSpentOnCall: (
                    Number(dx.timeSpentOnCall) /
                    1000 /
                    60
                  ).toFixed(1),
                };
              } else {
                return { count: 0, duration: 0, timeSpentOnCall: 0 };
              }
            }),
          };
        });
        this.reports = eachMembersData;

        console.log("this.reports", JSON.stringify(this.reports));

        // Show latest date first
        dates.reverse();
        _.each(this.reports, (r) => {
          if (Array.isArray(r.data)) r.data.reverse();
        });
        this.rangeOfDates = dates;

        this.loading = false;
        this.loadingText = "";
      } catch (e) {
        console.log("Error", e);
        this.loading = false;
        this.loadingText = "Error while loading your data";
      }
    },
    getUserById(id) {
      for (var u in this.allUsers) {
        if (this.allUsers[u].id == id) {
          return this.allUsers[u];
        }
      }
    },

    // Export CSV
    exportReport() {
      try {
        const self = this;
        var exportData = self.reports;
        self.loader = true;
        exportData = _.map(exportData, (d) => {
          var i = 0;
          var payload = [];
          while (i < 3) {
            var y = {
              Name: d.name.toString(),
              Email: d.email || "",
            };
            // Ensure date headers are formatted correctly
            for (var x in self.rangeOfDates) {
              const formattedDate = moment(
                self.rangeOfDates[x],
                "DD-MM-YYYY",
              ).format("DD-MM-YYYY");

              if (i == 0) {
                y.Type = "Call";
                y[formattedDate] = d.data[x].count;
              } else if (i == 1) {
                y.Type = "Duration (M)";
                y[formattedDate] = d.data[x].duration;
              } else {
                y.Type = "TimeSpend (M)";
                y[formattedDate] = d.data[x].timeSpentOnCall;
              }
            }
            i++;
            payload.push(y);
          }
          return payload;
        });
        exportData = _.flattenDepth(exportData);
        if (exportData.length < 1) {
          self.loader = false;
          self.snackbar = true;
          self.snackbarText = "No records to export";
          self.color = "#cc9900";
          return;
        }
        var csv = Papa.unparse(exportData);
        var csvData = new Blob([csv], {
          type: "text/csv;charset=utf-8;",
        });
        var csvURL = null;
        if (navigator.msSaveBlob) {
          csvURL = navigator.msSaveBlob(
            csvData,
            `report_${new Date().getTime()}.csv`,
          );
        } else {
          csvURL = window.URL.createObjectURL(csvData);
        }
        var tempLink = document.createElement("a");
        tempLink.href = csvURL;
        tempLink.setAttribute("download", `report_${new Date().getTime()}.csv`);
        tempLink.click();

        self.loader = false;
      } catch (e) {
        console.log(e);
        self.loader = false;
        self.snackbar = true;
        self.snackbarText = "Something went wrong while exporting reports.";
        self.color = "error";
      }
    },

    getImage(pic) {
      return `${this.$store.state.ENDPOINT}/storages/get/${pic}?access_token=${this.$store.getters.user.token}`;
    },
  },
};
</script>

<style scoped>
.attendance-container {
  background-color: var(--v-background-base, #f5f5f5);
  min-height: 100vh;
}

.theme--dark .attendance-container {
  background-color: #121212 !important;
}

.feature-chip {
  background: rgba(255, 255, 255, 0.25) !important;
  color: white !important;
  border-color: rgba(255, 255, 255, 0.4) !important;
  backdrop-filter: blur(10px);
  font-weight: 500;
}

.modern-card {
  background: white;
  border-radius: 12px;
  position: relative;
  overflow: visible;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.modern-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.card-header-green {
  height: 4px;
  background: linear-gradient(90deg, #37cf37 0%, #2ba82b 100%);
  width: 100%;
}

.customBorderRadius {
  border-radius: 12px !important;
}

.v-btn {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.25px;
}

.filter-card-title {
  align-items: center;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
}

.filter-title-copy {
  align-items: center;
  display: flex;
  min-width: 0;
}

.productivity-classic-filter-toggle {
  justify-self: center;
}

.office-timing-filter-action {
  justify-self: end;
}

.classic-view-toggle {
  align-items: center;
  background: transparent;
  border: 0;
  color: #333833;
  cursor: pointer;
  display: inline-flex;
  font-size: 13px;
  font-weight: 600;
  gap: 10px;
  height: 40px;
  justify-content: center;
  padding: 0 4px;
  white-space: nowrap;
}

.classic-view-toggle:focus-visible {
  border-radius: 999px;
  box-shadow: 0 0 0 3px rgba(25, 155, 56, 0.2);
  outline: none;
}

.classic-view-toggle:hover .classic-view-toggle-label {
  color: #199b38;
}

.classic-view-toggle-label {
  color: #6f746e;
  transition: color 0.18s ease;
}

.classic-view-classic-label {
  color: #199b38;
}

.classic-view-switch {
  background: #cfd4cd;
  border-radius: 999px;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.04);
  display: inline-flex;
  height: 20px;
  padding: 2px;
  transition: background 0.18s ease;
  width: 40px;
}

.classic-view-switch-thumb {
  background: #ffffff;
  border-radius: 50%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.22);
  height: 16px;
  transition: transform 0.18s ease;
  width: 16px;
}

.classic-view-toggle-active .classic-view-switch {
  background: #199b38;
}

.classic-view-toggle-active .classic-view-classic-label {
  color: #6f746e;
}

.classic-view-toggle-active .classic-view-modern-label {
  color: #199b38;
}

.classic-view-toggle-active .classic-view-switch-thumb {
  transform: translateX(18px);
}

.h3 {
  font-size: 17px;
  font-weight: 400;
  letter-spacing: 0.5px;
}

/* ===== Modern themed table (matches summary.vue) ===== */
.modern-table {
  border-radius: 0 !important;
  background: transparent !important;
}

.modern-table ::v-deep table {
  background: transparent !important;
}

.modern-table ::v-deep thead tr {
  background: #f6faf6;
}

.theme--dark .modern-table ::v-deep thead tr {
  background: #232323 !important;
}

.modern-table ::v-deep thead th {
  font-weight: 600 !important;
  font-size: 12px !important;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #4a4a4a !important;
  border-bottom: 1px solid #e6e6e6 !important;
  white-space: nowrap;
}

.theme--dark .modern-table ::v-deep thead th {
  color: #d0d0d0 !important;
  border-bottom-color: #333333 !important;
}

.modern-table ::v-deep tbody tr:hover {
  background: rgba(55, 207, 55, 0.06) !important;
}

.theme--dark .modern-table ::v-deep tbody tr:hover {
  background: rgba(55, 207, 55, 0.12) !important;
}

.modern-table ::v-deep tbody td {
  border-bottom: 1px solid #efefef !important;
}

.theme--dark .modern-table ::v-deep tbody td {
  border-bottom-color: #2a2a2a !important;
  color: #e0e0e0;
}

/* Per-day cell */
.day-cell {
  background: #f6faf6 !important;
  border: 1px solid #e6f1e6;
  border-radius: 10px !important;
  min-width: 130px;
  padding: 8px 6px;
  transition: all 0.2s ease;
}

.day-cell:hover {
  background: #eef9ee !important;
  border-color: #c9e6c9;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(55, 207, 55, 0.12);
}

.theme--dark .day-cell {
  background: #262d27 !important;
  border-color: #2f3a30 !important;
  color: #e0e0e0;
}

.theme--dark .day-cell:hover {
  background: #2c352d !important;
  border-color: #3b4b3c !important;
}

.day-stat {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 12px;
  line-height: 1.4;
  padding: 2px 0;
  color: inherit;
}

.day-stat .stat-label {
  font-weight: 500;
  white-space: nowrap;
}

.day-divider {
  border-color: rgba(55, 207, 55, 0.18) !important;
  margin: 2px 6px !important;
}

.theme--dark .day-divider {
  border-color: rgba(55, 207, 55, 0.3) !important;
}

.member-name {
  font-size: 14px;
  font-weight: 500;
  margin-top: 6px;
  color: inherit;
}

/* Sticky Members column */
.modern-table ::v-deep .v-data-table__wrapper,
.modern-table ::v-deep > div {
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: #b7e3b7 transparent;
}

.modern-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar,
.modern-table ::v-deep > div::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}

.modern-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-track,
.modern-table ::v-deep > div::-webkit-scrollbar-track {
  background: transparent;
}

.modern-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb,
.modern-table ::v-deep > div::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #6ed46e, #37cf37);
  border-radius: 8px;
  border: 2px solid transparent;
  background-clip: padding-box;
}

.modern-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb:hover,
.modern-table ::v-deep > div::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #37cf37, #2ba82b);
  background-clip: padding-box;
  border: 2px solid transparent;
}

.theme--dark .modern-table ::v-deep .v-data-table__wrapper,
.theme--dark .modern-table ::v-deep > div {
  scrollbar-color: #2ba82b transparent;
}

.theme--dark .modern-table ::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb,
.theme--dark .modern-table ::v-deep > div::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #2ba82b, #1e7a1e);
  background-clip: padding-box;
}

.modern-table ::v-deep th.sticky-col,
.modern-table ::v-deep td.sticky-col {
  position: sticky;
  left: 0;
  z-index: 2;
  background: #ffffff;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.04);
  min-width: 140px;
}

.modern-table ::v-deep thead th.sticky-col {
  z-index: 3;
  background: #f6faf6;
}

.theme--dark .modern-table ::v-deep th.sticky-col,
.theme--dark .modern-table ::v-deep td.sticky-col {
  background: #1e1e1e;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.4);
}

.theme--dark .modern-table ::v-deep thead th.sticky-col {
  background: #232323;
}

.modern-table ::v-deep tbody tr:hover td.sticky-col {
  background: #f1faf1;
}

.theme--dark .modern-table ::v-deep tbody tr:hover td.sticky-col {
  background: #243124;
}

/* Dark theme for the wrapping cards */
.theme--dark .modern-card {
  background: #1e1e1e !important;
  color: #e0e0e0;
}

.theme--dark .h3 {
  color: #d0d0d0;
}

@media (max-width: 960px) {
  .filter-card-title {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
  }

  .productivity-classic-filter-toggle {
    align-self: center;
  }

  .office-timing-filter-action {
    align-self: flex-start;
  }

  .page-title {
    font-size: 22px;
  }

  .header-icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 12px;
  }

  .header-icon-wrapper .v-icon {
    font-size: 24px !important;
  }

  .feature-pills-container {
    gap: 8px;
  }

  .feature-pill {
    padding: 8px 16px;
    font-size: 12px;
  }
}
</style>
